import { Link as LinkIcon } from 'iconoir-react'
import { capitalize } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import { ActivityCard } from './ActivityCard'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import type { ActivityJson } from 'app/types/entities'
import type { PatientJson } from 'app/types/entities'
import { assertBoom } from 'lib/assertBoom'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

type Props = {
  activity: ActivityJson
  isFirst?: boolean
  isLast?: boolean
}
/**
 * Renders Patient activity record.
 * @Prop activity - Activity to query the Form from.
 * @Prop isFirst. Boolean. If true it hides the first activity union line.
 * @Prop isLast. Boolean. If true it hides the last activity union line.
 * @returns ReactElement
 */
export function PatientCard({ activity, isFirst, isLast }: Props): ReactElement {
  assertBoom(activity.type === `Patient`)
  const [patient, setPatient] = useState<PatientJson>()

  useEffect(() => {
    if (!activity.typeId) return
    bg(async () => {
      try {
        const result = await gqlFetchOne<PatientJson>(`patient`, activity.typeId!, `id firstName lastName pmsPatientId`)
        if (result) setPatient(result)
      } catch (err) {
        logError(`PatientCard gqlFetchOne error`, err)
      }
    })
  }, [activity.typeId])

  return (
    <ActivityCard
      title={
        <span className="break-all text-left text-ui-sm text-strong">
          Patient Linked<span className="text-body-sm text-subtle">{` from PMS`}</span>
          {patient?.pmsPatientId
            ? `: ${capitalize(patient.firstName)} ${capitalize(patient.lastName)} (ID:${patient.pmsPatientId})`
            : ``}
        </span>
      }
      activityDate={activity.activityDate}
      icon={<LinkIcon className="h-5 w-5 rounded-md bg-inverse p-1 text-inverse" />}
      isFirst={isFirst}
      isLast={isLast}
      hideChevronIcon
    />
  )
}
